import { storefrontAppearanceReadyAtom } from "@/atoms/storefront-appearance";
import type { StorefrontColors } from "@/domain/storefront-appearance";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { dismissInitialLoading } from "@/lib/initial-loading";
import { runIrisTransition, viewportTopLeft } from "@/lib/iris-transition";
import { applyStorefrontColors } from "@/lib/theme";
import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";

/**
 * Flag on `<html>` meaning "storefront palette is known". Mirrored in
 * `globals.css`, which enables the skeleton when unset. Renaming here without
 * renaming there silently kills the storewide skeleton.
 */
const APPEARANCE_READY_ATTR = "data-appearance-ready";

/**
 * Paints the storefront with merchant-chosen colors. Called ONCE at the tree
 * top. Writes CSS variables on `<html>` instead of prop-drilling seven roles
 * across five screens. No visible effect while defaults match `globals.css`.
 */
export function useStorefrontColors(): void {
  const { colors } = useStorefrontAppearance();
  const appearanceReady = useAtomValue(storefrontAppearanceReadyAtom);
  /** Last palette that actually landed on `<html>`. */
  const painted = useRef<StorefrontColors | null>(null);

  useEffect(() => {
    if (!appearanceReady) return;

    const root = document.documentElement;

    if (!painted.current) {
      painted.current = colors;
      applyStorefrontColors(colors);
      root.setAttribute(APPEARANCE_READY_ATTR, "");
      dismissInitialLoading(viewportTopLeft());
      return;
    }

    if (sameColors(painted.current, colors)) return;
    painted.current = colors;

    runIrisTransition(viewportTopLeft(), () => applyStorefrontColors(colors));
  }, [appearanceReady, colors]);

  useEffect(
    () => () => document.documentElement.removeAttribute(APPEARANCE_READY_ATTR),
    [],
  );
}

/**
 * Compares roles one-by-one, not by object identity: appearance reads always
 * return a new object even when equal to defaults. By reference, "unchanged"
 * would never detect and the iris would run on every open.
 */
function sameColors(left: StorefrontColors, right: StorefrontColors): boolean {
  return (Object.keys(left) as (keyof StorefrontColors)[]).every(
    (role) => left[role] === right[role],
  );
}
